
import {messages as messagesEnUS} from '@app/features/i18n/locales/en-US/messages.mjs';
import AppStorage from '@app/features/platform/state/PersistentStorage';
import {getNativeLocaleIdentifier} from '@app/features/platform/types/Platform';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {loadLazyModule} from '@app/features/platform/utils/LazyModuleLoader';
import {type I18n, i18n, type Messages} from '@lingui/core';
import {createAtom, runInAction} from 'mobx';

// ÜRÜN KARARI (2026-07-29, kullanıcı: "sadece türkçe ve ingilizce olucak"):
// kaynak-referansın 35-locale listesi 2'ye kısıtlandı — yapı/akış kaynak-referans-1-1 kaldı,
// yalnız liste + loaders daraltıldı. Yeni dil eklenecekleri gün kaynak-referans
// src/app/I18n.ts listesinden geri kopyalanır (lingui.config.js notuna bak).
const supportedLocales = ['en-US', 'tr'] as const;

type LocaleCode = (typeof supportedLocales)[number];

const DEFAULT_LOCALE: LocaleCode = 'en-US';
const supportedLocaleSet = new Set<LocaleCode>(supportedLocales);
const logger = new Logger('i18n');
const LANGUAGE_OVERRIDES: Record<string, LocaleCode> = {
	en: 'en-US',
};

type LocaleLoader = () => Promise<{
	messages: Messages;
}>;

const loaders: Record<LocaleCode, LocaleLoader> = {
	'en-US': () => Promise.resolve({messages: messagesEnUS}),
	tr: () => import('@app/features/i18n/locales/tr/messages.mjs'),
};

function formatLocaleValue(value: string): string {
	const trimmed = value.trim();
	if (!trimmed) {
		return '';
	}
	const segments = trimmed.split(/[-_]/).filter(Boolean);
	if (segments.length === 0) {
		return '';
	}
	const language = segments[0].toLowerCase();
	if (segments.length === 1) {
		return language;
	}
	const region = segments
		.slice(1)
		.map((segment) => segment.toUpperCase())
		.join('-');
	return `${language}-${region}`;
}

export function normalizeLocale(value?: string | null): LocaleCode {
	if (!value) {
		return DEFAULT_LOCALE;
	}
	const formatted = formatLocaleValue(value);
	if (!formatted) {
		return DEFAULT_LOCALE;
	}
	if (supportedLocaleSet.has(formatted as LocaleCode)) {
		return formatted as LocaleCode;
	}
	const [language] = formatted.split('-');
	if (!language) {
		return DEFAULT_LOCALE;
	}
	const override = LANGUAGE_OVERRIDES[language];
	if (override) {
		return override;
	}
	const fallback = supportedLocales.find((code) => code.split('-')[0].toLowerCase() === language);
	if (fallback) {
		return fallback;
	}
	return DEFAULT_LOCALE;
}

function detectBrowserLocale(): string | null {
	if (Array.isArray(navigator.languages) && navigator.languages.length > 0) {
		return navigator.languages[0];
	}
	return navigator.language ?? null;
}

// PROD-HAZIRLIK P3 (IP'ye göre varsayılan dil, scratchpad/i18n_raporlar/
// prod_3_dis_hesaplar.md §3) — tebhos_app_proxy (bkz. tebhos_app_proxy/src/
// locale.rs) her SPA-shell isteğinde Accept-Language başlığını, o yoksa
// Cloudflare'in CF-IPCountry başlığını, o da yoksa yerel GeoIP (mmdb)
// sonucunu kullanarak bir "önerilen dil" hesaplayıp
// window.__TEBHOS_BOOTSTRAP__.suggestedLocale'e enjekte eder (payload'ın
// KÖKÜNDE, config'in İÇİNDE DEĞİL — bkz. tebhos_app_proxy/src/bootstrap.rs
// BootstrapPayload). Bu sunucu tarafında hesaplanan öneri, aşağıdaki
// navigator-tabanlı algılamadan (nativeLocale/browserLocale — ikisi de
// yalnızca Accept-Language'in tarayıcı-taraflı karşılığı) daha zengindir
// çünkü Accept-Language yokken bile bir sinyal üretebilir. Kayıtlı bir
// kullanıcı tercihi (storedLocale) HER ZAMAN bunun önüne geçer.
function getBootstrapSuggestedLocale(): string | null {
	if (typeof window === 'undefined') {
		return null;
	}
	const suggested = window.__TEBHOS_BOOTSTRAP__?.suggestedLocale;
	return typeof suggested === 'string' && suggested.trim() ? suggested : null;
}

function detectPreferredLocale(forceLocale?: string): LocaleCode {
	if (forceLocale) {
		return normalizeLocale(forceLocale);
	}
	const storedLocale = AppStorage.getItem('locale');
	if (storedLocale) {
		return normalizeLocale(storedLocale);
	}
	const suggestedLocale = getBootstrapSuggestedLocale();
	if (suggestedLocale) {
		return normalizeLocale(suggestedLocale);
	}
	const nativeLocale = getNativeLocaleIdentifier();
	if (nativeLocale) {
		return normalizeLocale(nativeLocale);
	}
	const browserLocale = detectBrowserLocale();
	if (browserLocale) {
		return normalizeLocale(browserLocale);
	}
	return DEFAULT_LOCALE;
}

/**
 * KALICILAŞTIRMA ARTIK SEÇİMLİ (22 Eyl 2026 düzeltmesi).
 *
 * Eskiden bu fonksiyon HER etkinleştirmede dili localStorage'a yazıyordu —
 * `initI18n`in HATA YOLU dahil. Sonuç: katalog yüklemesi BİR KEZ başarısız
 * olursa (geçici ağ/chunk hatası) yedek dil `en-US` kalıcı olarak yazılıyor;
 * bir sonraki açılışta `detectPreferredLocale` saklı değeri en üstte okuduğu
 * için tarayıcı Türkçe olsa bile İngilizce kazanıyor ve kullanıcı dilini bir
 * daha geri alamıyor (yapışkan). Yerelde gerçekten yaşandı: uygulama geçici
 * olarak açılmaz haldeyken katalog yüklemesi patladı, dil kalıcı İngilizce oldu.
 *
 * Artık yalnız GERÇEK bir seçim/çözümleme kalıcılaşır; hata yedeği yazmaz.
 * BİLİNÇLİ SAPMA: kaynak-referans hata yolunda da yazıyor — orada da aynı
 * yapışkanlık var, ama kullanıcı dilini kaybetmek kabul edilebilir değil.
 */
function activateLocale(localeCode: LocaleCode, messages: Messages, persist = true): void {
	i18n.loadAndActivate({locale: localeCode, messages});
	if (typeof document !== 'undefined') {
		document.documentElement.lang = localeCode;
	}
	if (persist) {
		AppStorage.setItem('locale', localeCode);
	}
}

const inFlightLoads = new Map<LocaleCode, Promise<LocaleCode>>();

export async function loadLocaleCatalog(localeCode: string): Promise<LocaleCode> {
	const normalized = normalizeLocale(localeCode);
	const inFlight = inFlightLoads.get(normalized);
	if (inFlight) {
		return inFlight;
	}
	const loadPromise = (async () => {
		try {
			const {messages} = await loadLazyModule(loaders[normalized]);
			activateLocale(normalized, messages);
			return normalized;
		} finally {
			inFlightLoads.delete(normalized);
		}
	})();
	inFlightLoads.set(normalized, loadPromise);
	return loadPromise;
}

export function applyLocaleChange(localeCode: string): LocaleCode {
	const normalized = normalizeLocale(localeCode);
	if (normalized === i18n.locale) {
		AppStorage.setItem('locale', normalized);
		return normalized;
	}
	AppStorage.setItem('locale', normalized);
	void loadLocaleCatalog(normalized).catch((error) => {
		logger.error(`Failed to apply locale ${normalized}`, error);
	});
	return normalized;
}

let initPromise: Promise<typeof i18n> | null = null;

export async function initI18n(forceLocale?: string) {
	if (!initPromise) {
		initPromise = (async () => {
			try {
				const localeToLoad = detectPreferredLocale(forceLocale);
				await loadLocaleCatalog(localeToLoad);
			} catch (error) {
				logger.error('Failed to initialize i18n, falling back to default locale', error);
				// Yedek dil KALICILAŞTIRILMAZ: geçici bir hata kullanıcının dil
				// tercihini kalıcı olarak değiştirmemeli (bkz. activateLocale).
				activateLocale(DEFAULT_LOCALE, messagesEnUS, false);
			}
			return i18n;
		})();
	}
	return initPromise;
}

const localeAtom = createAtom('i18nLocale');
const localeSensitiveProps = new Set<PropertyKey>(['_', 't', 'date', 'number', 'locale', 'locales', 'messages']);
const boundMethods = new Map<PropertyKey, unknown>();

const reactiveI18n = new Proxy(i18n, {
	get(target, prop) {
		if (localeSensitiveProps.has(prop)) {
			localeAtom.reportObserved();
		}
		const value = Reflect.get(target, prop);
		if (typeof value !== 'function') {
			return value;
		}
		let bound = boundMethods.get(prop);
		if (!bound) {
			bound = value.bind(target);
			boundMethods.set(prop, bound);
		}
		return bound;
	},
}) as I18n;

i18n.on('change', () => {
	try {
		runInAction(() => {
			localeAtom.reportChanged();
		});
	} catch (error) {
		logger.error('Failed to invalidate locale atom:', error);
	}
});

export default reactiveI18n;
